/* eslint-disable max-len */
import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useHistory } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown } from 'primereact/dropdown';
import { useSelector } from 'react-redux';
import { withAccount } from '../../../components/AccountContext';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { growlShape } from '../../../components/prop-types';
import FileImportDropzone from '../../../components/FileImportDropzone';
import { Loading } from '../../../components/Loading';
import { BEHAVIOR_SECTION, COGNITIVE_SECTION } from '../constants';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_LANGUAGE_ACTION_TYPE = 'SET_LANGUAGE';
const SET_SECTION_ACTION_TYPE = 'SET_SECTION';

const orderAlphabetically = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

const i18nMessages = defineMessages({
  importAssessmentCustomTranslations: {
    id: 'menu.item-bank-management.import-assessment-custom-translations',
    defaultMessage: 'Import Assessment Custom Translations',
  },
  downloadTemplate: {
    id: 'res_import.download_hint',
    defaultMessage: 'Download Import Template file',
  },
});

const CONFIG_QUERY = gql`
  query configQuery($id: ID!) {
    addItemBankContentConfig(id: $id) {
      availableSections {
        code
        displayName
      }
    }
    activeLocales
  }
`;

const UPDATE_CUSTOM_ASSESSMENT_BEHAVIOR_TRANSLATIONS_MUTATION = gql`
  mutation updateCustomAssessmentBehaviorTranslationsMutation($input: UpdateCustomTranslationsInput!) {
    updateCustomAssessmentBehaviorTranslations(input: $input) {
      accountId
      language
    }
  }
`;

const UPDATE_CUSTOM_ASSESSMENT_COGNITIVE_TRANSLATIONS_MUTATION = gql`
  mutation updateCustomAssessmentCognitiveTranslationsMutation($input: UpdateCustomTranslationsInput!) {
    updateCustomAssessmentCognitiveTranslations(input: $input) {
      accountId
      language
    }
  }
`;

const UpdateAssessmentCustomTranslations = ({ currentAccount, growl }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [updateCustomAssessmentBehaviorTranslationsMutation] = useMutation(UPDATE_CUSTOM_ASSESSMENT_BEHAVIOR_TRANSLATIONS_MUTATION);
  const [updateCustomAssessmentCognitiveTranslationsMutation] = useMutation(UPDATE_CUSTOM_ASSESSMENT_COGNITIVE_TRANSLATIONS_MUTATION);

  const {
    cldr: { localeDisplayName },
  } = useSelector(state => state.i18n);

  const initialState = {
    selectedLanguage: null,
    supportedLanguages: [],
    selectedSection: null,
    availableSections: [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          selectedLanguage: action.payload.selectedLanguage,
          supportedLanguages: action.payload.supportedLanguages,
          selectedSection: action.payload.selectedSection,
          availableSections: action.payload.availableSections,
        };
      case SET_LANGUAGE_ACTION_TYPE:
        return {
          ...state,
          selectedLanguage: action.payload.selectedLanguage,
        };
      case SET_SECTION_ACTION_TYPE:
        return {
          ...state,
          selectedSection: action.payload.selectedSection,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const { loading, error, data } = useQuery(CONFIG_QUERY, { variables: { id: currentAccount } });

  const { addItemBankContentConfig, activeLocales } = data;

  useEffect(() => {
    const dataIsAvailable = addItemBankContentConfig && activeLocales && activeLocales.length > 0;

    if (dataIsAvailable) {
      const languages = activeLocales
        .map(lang => ({
          value: lang,
          label: localeDisplayName(lang),
        }))
        .sort(orderAlphabetically);

      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          selectedLanguage: languages[0].value,
          supportedLanguages: languages,
          selectedSection: addItemBankContentConfig.availableSections[0].code,
          availableSections: addItemBankContentConfig.availableSections,
        },
      };

      dispatch(action);
    }
  }, [addItemBankContentConfig, activeLocales, localeDisplayName]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const {
    selectedLanguage, supportedLanguages, selectedSection, availableSections,
  } = state;

  const onUpload = async (files) => {
    const file = files[0];

    const input = {
      variables: {
        input: {
          accountId: currentAccount,
          // for using file drop component, the field name need to be mapped
          // to the input id, in this case, it's always 1
          fieldName: '1',
          language: selectedLanguage,
        },
        file,
      },
    };

    if (selectedSection === BEHAVIOR_SECTION) {
      try {
        await updateCustomAssessmentBehaviorTranslationsMutation(input).then(() => growlActionSuccess(growl));
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }

    if (selectedSection === COGNITIVE_SECTION) {
      try {
        await updateCustomAssessmentCognitiveTranslationsMutation(input).then(() => growlActionSuccess(growl));
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }
  };

  const sectionOptions = availableSections.map(section => ({
    value: section.code,
    label: section.displayName,
  }));

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={formatMessage(i18nMessages.importAssessmentCustomTranslations)}>
            <div className="p-grid">
              <div className="p-col-3">
                <FileImportDropzone
                  onUpload={onUpload}
                  onCancel={() => history.push(`/accounts/${currentAccount}/dashboard`)}
                />
              </div>
              <div className="p-col-3 import-custom-translations-dropdown-container">
                <Dropdown
                  className="dropdown"
                  filter
                  filterBy="label"
                  value={selectedLanguage}
                  options={supportedLanguages}
                  onChange={(event) => {
                    const action = {
                      type: SET_LANGUAGE_ACTION_TYPE,
                      payload: {
                        selectedLanguage: event.value,
                      },
                    };

                    dispatch(action);
                  }}
                />
                <Dropdown
                  className="dropdown"
                  filter
                  filterBy="label"
                  value={selectedSection}
                  options={sectionOptions}
                  onChange={(event) => {
                    const action = {
                      type: SET_SECTION_ACTION_TYPE,
                      payload: {
                        selectedSection: event.value,
                      },
                    };

                    dispatch(action);
                  }}
                />
              </div>
              <div className="p-col-12">
                <a target="_blank" href="/import-templates/import-assessment-custom-translations.xlsx">
                  {formatMessage(i18nMessages.downloadTemplate)}
                </a>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

UpdateAssessmentCustomTranslations.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(withGrowl(UpdateAssessmentCustomTranslations));
